import React from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Avatar } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { adminMenuSections, wholesaleMenuSections } from '../config/navigation';
import { Menu as MenuIcon } from '@mui/icons-material';

const drawerWidth = 256;

const Sidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const getInitials = (name) => {
    if (!name) return 'A';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      {/* Navigation */}
      <List sx={{ flex: 1 }}>
        {(() => {
        const sections = user?.is_wholesale_client ? wholesaleMenuSections : adminMenuSections;
        // Determinar la ruta activa más específica (la coincidencia más larga gana)
        const activePath = sections
          .flatMap((s) => s.items.map((i) => i.path))
          .filter((p) => location.pathname === p || location.pathname.startsWith(p + '/'))
          .sort((a, b) => b.length - a.length)[0];
        return sections
          .map((section) => {
            const visibleItems = section.items.filter(item => hasPermission(item.permission));
            if (visibleItems.length === 0) return null;
            return (
              <React.Fragment key={section.title || 'top'}>
                {section.title && <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    px: 2,
                    mt: 2,
                    mb: 0.5,
                    color: '#4e7397',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    lineHeight: 1.5,
                  }}
                >
                  {section.title}
                </Typography>}
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.path === activePath;
                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => {
                          if (item.external) {
                            window.open(item.path, '_blank', 'noopener');
                          } else {
                            navigate(item.path);
                          }
                          setMobileOpen(false);
                        }}
                        sx={{
                          borderRadius: '0.5rem',
                          backgroundColor: isActive ? 'rgba(128, 0, 32, 0.1)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          '& .MuiListItemIcon-root': {
                            color: isActive ? '#800020' : '#0e141b',
                            minWidth: 40,
                          },
                          '& .MuiListItemText-primary': {
                            color: isActive ? '#800020' : '#0e141b',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '0.875rem',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Icon />
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </React.Fragment>
            );
          });
        })()}
      </List>

      {/* Logout */}
      <Box sx={{ mt: 'auto' }}>
        {/* User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, pl: 1 }}>
          <Avatar
            sx={{
              bgcolor: '#800020',
              width: 40,
              height: 40,
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {getInitials(user?.first_name || user?.username || 'A')}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#0e141b' }}>
              {user?.first_name || user?.username || 'Admin'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#4e7397', fontSize: '0.75rem' }}>
              {user?.is_wholesale_client ? 'Comercio Mayorista' : (user?.is_staff ? 'Administrador' : 'Usuario')}
            </Typography>
          </Box>
        </Box>
        <ListItemButton
          onClick={onLogout}
          sx={{
            borderRadius: '0.5rem',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '& .MuiListItemIcon-root': {
                color: '#d32f2f',
              },
              '& .MuiListItemText-primary': {
                color: '#d32f2f',
              },
            },
            '& .MuiListItemIcon-root': {
              color: '#4e7397',
              minWidth: 40,
            },
            '& .MuiListItemText-primary': {
              color: '#4e7397',
              fontSize: '0.875rem',
            },
          }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Cerrar Sesión" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Botón menú mobile */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: 10,
          left: 10,
          zIndex: 1201,
          bgcolor: 'rgba(128, 0, 32, 0.9)',
          color: 'white',
          p: 1,
          borderRadius: '0.5rem',
          boxShadow: 3,
          cursor: 'pointer'
        }}
        onClick={handleDrawerToggle}
      >
        <MenuIcon />
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #d0dbe7',
            backgroundColor: '#f6f7f8',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #d0dbe7',
            backgroundColor: '#f6f7f8',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;
